/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

/**
 * Mapping entre `AdresseForm` (`@mairistem/adresses`) et l'adresse d'une
 * convention côté fiche (`adresseCommune`, `adresseDeclaration`,
 * `adressePacs`…), dont le modèle est { adresse, codePostal, localite,
 * libellePays, communeDelegue } — une simple ligne de rue, pas les champs
 * granulaires (numéro/voie/article séparés) utilisés par l'assistant de
 * création.
 *
 * Comme il n'y a qu'une ligne de texte en base, `toAdresseFormValue` la
 * découpe au mieux (numéro + indice de répétition + reste = voie) pour
 * pré-remplir séparément les champs Numéro/Voie à l'ouverture ; ce n'est
 * qu'une heuristique (pas de round-trip garanti pour une adresse saisie hors
 * de ce formulaire).
 */

import _ from 'lodash';

const INDICES_REPETITION = [
  'Bis',
  'Ter',
  'Quater',
  'Quinquies',
  'Sexies',
  'Septies',
  'Octies',
];

// Le complément de numéro (ex. « 12 B Rue de la Paix ») n'est repéré que sous
// forme d'une lettre/chiffre isolé (un seul caractère suivi d'un espace) :
// contrairement à un vrai début de voie (« Rue », « Le Bourg »…), toujours
// composé de mots d'au moins 2 lettres, ce qui évite de tronquer la voie.
const PARSE_ADRESSE_LINE = new RegExp(
  `^(\\d+)\\s*(${INDICES_REPETITION.join('|')})?\\s*([A-Za-z0-9](?=\\s))?\\s*(.*)$`,
  'i',
);

const parseAdresseLine = (line: string | undefined) => {
  const trimmed = (line ?? '').toString().trim();

  const match = trimmed.match(PARSE_ADRESSE_LINE);
  if (!match) {
    return {
      numeroVoirie: '',
      indiceRepetition: '',
      complementNumeroVoirie: '',
      voie: trimmed,
    };
  }

  const [, numero, indice, complement, voie] = match;
  const normalizedIndice =
    INDICES_REPETITION.find(
      (option) => option.toLowerCase() === indice?.toLowerCase(),
    ) ?? '';

  return {
    numeroVoirie: numero,
    indiceRepetition: normalizedIndice,
    complementNumeroVoirie: complement ?? '',
    voie,
  };
};

const toText = (value: unknown): string => (value ?? '').toString().trim();

// ETCV enregistre 0 en numéro de voie quand il n'y en a pas.
export const toNumeroVoie = (value: unknown): string => {
  const numero = toText(value);

  return numero === '0' ? '' : numero;
};

/**
 * Libellé de voie déduit de la ligne `adresse` (conventions historiques, sans
 * champs granulaires) : première ligne, sans le numéro de voie en tête.
 * Le `0` d'ETCV (« pas de numéro ») est retiré lui aussi : l'API l'a recopié
 * en tête des lignes qu'elle a composées (« 0 83190 Ollioules »).
 * Une ligne qui commence par un code postal n'a pas de voie.
 */
export const extractLibelleVoie = (
  adresseText: string | null | undefined,
  numeroVoie: unknown,
): string | null => {
  if (!adresseText) return null;

  const [firstLine] = adresseText.split(/\r?\n/);
  let voie = (firstLine ?? '').trim();

  const numero = toNumeroVoie(numeroVoie) || '0';
  if (voie === numero || voie.startsWith(`${numero} `)) {
    voie = voie.slice(numero.length).trim();
  }

  // Ligne « 83190 OLLIOULES » : adresse sans voie, le code postal n'est pas une voie.
  if (/^\d{5}\b/.test(voie)) return null;

  return voie || null;
};

// Type d'adresse : libellé d'`AdresseForm` ↔ code partagé avec ETCV
// (aa_type des adresses d'acte, ai_res_type de la résidence d'un individu).
const TYPE_ADRESSE_COMMUNE = 'Commune';

const CODES_TYPE_ADRESSE: Record<string, number> = {
  [TYPE_ADRESSE_COMMUNE]: 1,
  'Hors Commune': 2,
  Etranger: 3,
  Cedex: 4,
};

/**
 * Libellé `AdresseForm` du type d'adresse enregistré. Une adresse sans type
 * (créée par 360 avant sa prise en charge, ou 0 « non renseigné ») est
 * présentée en « Commune », le type par défaut du formulaire.
 */
export const toTypeAdresseLabel = (code: unknown): string =>
  _.findKey(CODES_TYPE_ADRESSE, (value) => value === Number(code)) ??
  TYPE_ADRESSE_COMMUNE;

/**
 * Seul le type « Commune » (défaut) affiche le champ Voie en recherche
 * assistée, avec le type de voie (« Rue », « Impasse »…) en étiquette. Dans
 * les autres types, la voie est en saisie libre sans étiquette : le type de
 * voie doit rester dans le libellé, sans quoi il disparaît de la saisie.
 */
export const estTypeAdresseCommune = (type: unknown): boolean =>
  !toText(type) || toText(type) === TYPE_ADRESSE_COMMUNE;

const PAYS_FRANCE = 'France';

/**
 * Pays imposé par le type d'adresse : « Commune » et « Hors Commune » sont en
 * France. AdresseForm y masque le champ Pays et, au changement de type, vide
 * celui de l'adresse précédente (Étranger, Cedex) ; il ne le remet à France
 * qu'à la sélection d'une commune du référentiel, pas en saisie libre.
 */
export const paysTypeAdresse = (type: unknown): string | null =>
  ['Commune', 'Hors Commune'].includes(toText(type)) ? PAYS_FRANCE : null;

export const toTypeAdresseCode = (label: unknown): number =>
  CODES_TYPE_ADRESSE[toText(label)] ?? CODES_TYPE_ADRESSE[TYPE_ADRESSE_COMMUNE];

const hasTypeVoiePrefix = (libelleVoie: string, typeVoie: string): boolean =>
  libelleVoie.toLowerCase().startsWith(`${typeVoie.toLowerCase()} `);

const startsWithTypeVoie = (libelleVoie: string, typeVoie: string): boolean =>
  libelleVoie.toLowerCase() === typeVoie.toLowerCase() ||
  hasTypeVoiePrefix(libelleVoie, typeVoie);

/**
 * `AdresseForm` renvoie le type de voie (« Rue ») à part du libellé
 * (« Ernest RENAN »), mais ec_acte_adresse n'a pas de colonne pour le type :
 * comme ETCV, on l'enregistre en tête du libellé (« Rue Ernest RENAN »), sans
 * quoi il est perdu dès que la voie n'est pas rattachée au référentiel.
 */
export const composeLibelleVoie = (
  typeVoie: unknown,
  libelleVoie: unknown,
): string => {
  const type = toText(typeVoie);
  const libelle = toText(libelleVoie);

  if (!type || !libelle || startsWithTypeVoie(libelle, type)) {
    return libelle;
  }

  return `${type} ${libelle}`;
};

/**
 * Sens inverse de `composeLibelleVoie`, au chargement dans `AdresseForm` : le
 * type de voie y est affiché en étiquette du champ Voie, le laisser dans le
 * libellé le ferait apparaître deux fois.
 */
export const stripTypeVoie = (
  libelleVoie: unknown,
  typeVoie: unknown,
): string => {
  const type = toText(typeVoie);
  const libelle = toText(libelleVoie);

  if (!type || !startsWithTypeVoie(libelle, type)) {
    return libelle;
  }

  return libelle.slice(type.length).trim();
};

/**
 * Retrouve le type de voie (« Impasse ») en tête d'un libellé enregistré sans
 * type à part (« Impasse DES PIGNATEU »), pour l'afficher en étiquette du champ
 * Voie d'`AdresseForm`. Le plus long type correspondant l'emporte (« Grande
 * Rue » plutôt que « Grande »). Un libellé qui est lui-même un type (« Grande
 * Rue ») est laissé tel quel.
 */
export const findTypeVoie = (
  libelleVoie: unknown,
  typesVoies: string[],
): string | null => {
  const libelle = toText(libelleVoie);

  if (!libelle) return null;

  const estUnType = typesVoies.some(
    (typeVoie) => toText(typeVoie).toLowerCase() === libelle.toLowerCase(),
  );
  if (estUnType) return null;

  return (
    _.maxBy(
      typesVoies.filter(
        (typeVoie) => toText(typeVoie) && hasTypeVoiePrefix(libelle, typeVoie),
      ),
      'length',
    ) ?? null
  );
};

const nullifyEmpty = (value: unknown): unknown =>
  value === '' || value === undefined ? null : value;

const identity = (value: unknown): unknown => value;

/**
 * Informations du référentiel transverse remontées par `AdresseForm` dans son
 * état (clé état AdresseForm → clé adresse d'acte). Elles permettent de
 * rattacher l'adresse enregistrée à la voie / commune / code postal du
 * référentiel
 */
const REFERENTIEL_FIELDS: ReadonlyArray<
  readonly [string, string, (value: unknown) => unknown]
> = [
  ['identifiantVoie', 'identifiantVoie', identity],
  ['identifiantTypeVoie', 'identifiantTypeVoie', identity],
  ['typeVoie', 'typeVoie', nullifyEmpty],
  ['lieuDit', 'lieuDit', nullifyEmpty],
  ['identifiantCommune', 'identifiantCommune', identity],
  ['identifiantCodePostal', 'identifiantCodePostal', identity],
  ['identifiantDepartement', 'identifiantDepartement', identity],
  ['departement', 'libelleDepartement', nullifyEmpty],
];

/**
 * `AdresseForm` ne nettoie pas les identifiants quand l'utilisateur retape
 * par-dessus un libellé déjà sélectionné (la voie reste en saisie libre) ou
 * quand il vide un champ : seul le libellé est réémis, l'identifiant de la
 * sélection précédente reste dans l'état. Tant qu'on jetait les identifiants
 * c'était invisible ; maintenant qu'on les persiste, ce serait un identifiant
 * pointant vers une autre rue que le libellé enregistré.
 *
 * `codeDepartement` n'est pas invalidé : `AdresseForm` le recalcule lui-même à
 * chaque sélection de voie / commune / code postal. `article` non plus : c'est
 * un champ de saisie libre dans les modales de la fiche, l'écraser détruirait
 * une saisie manuelle.
 */
const INVALIDATE_ON_LIBELLE_CHANGE: ReadonlyArray<{
  readonly libelle: string;
  readonly identifiant: string;
  readonly dependants: readonly string[];
}> = [
  {
    libelle: 'voie',
    identifiant: 'identifiantVoie',
    dependants: ['identifiantVoie', 'identifiantTypeVoie', 'typeVoie', 'lieuDit'],
  },
  {
    libelle: 'codePostal',
    identifiant: 'identifiantCodePostal',
    dependants: ['identifiantCodePostal'],
  },
  {
    libelle: 'commune',
    identifiant: 'identifiantCommune',
    dependants: [
      'identifiantCommune',
      'identifiantDepartement',
      'libelleDepartement',
    ],
  },
];

export type AdresseFormValueOptions = {
  /**
   * Champ portant l'article de la localité : `lettre` pour les adresses de
   * convention / déclaration / commune, `articleLocalite` pour celles de PACS
   * et de dissolution.
   */
  articleField?: 'lettre' | 'articleLocalite';
};

type FromReferentielOptions = AdresseFormValueOptions & {
  /**
   * État précédent du formulaire, pour détecter qu'un libellé a été saisi
   * librement par-dessus une sélection et invalider les identifiants devenus
   * faux. Non fourni côté assistant, qui n'envoie que des patches mono-champ.
   */
  previous?: any;
};

/**
 * Informations du référentiel à enregistrer sur l'adresse d'acte.
 *
 * Les clés dont la valeur est `undefined` dans l'état du formulaire sont
 * **omises** (et non écrites à `null`) : c'est ce qui permet d'appeler cette
 * fonction aussi bien avec l'état complet (`onItemChange`, côté fiche) qu'avec
 * un patch mono-champ (`onChange`, côté assistant) sans écraser les clés
 * voisines de l'adresse existante.
 */
export const fromAdresseFormReferentiel = (
  next: any,
  options: FromReferentielOptions = {},
): Record<string, unknown> => {
  const { previous, articleField = 'lettre' } = options;

  const referentiel: Record<string, unknown> = {};

  REFERENTIEL_FIELDS.forEach(([formKey, acteKey, transform]) => {
    if (next?.[formKey] !== undefined) {
      referentiel[acteKey] = transform(next[formKey]);
    }
  });

  if (next?.article !== undefined) {
    referentiel[articleField] = nullifyEmpty(next.article);
  }

  if (!previous) {
    return referentiel;
  }

  INVALIDATE_ON_LIBELLE_CHANGE.forEach(
    ({ libelle, identifiant, dependants }) => {
      // Rien à invalider s'il n'y avait aucune sélection avant : écrire des
      // `null` sur des clés absentes de l'adresse ferait basculer le panneau en
      // « modifié » sans raison.
      const hadIdentifiant = previous[identifiant] !== null
        && previous[identifiant] !== undefined;
      if (!hadIdentifiant) return;

      const libelleChanged = next?.[libelle] !== previous[libelle];
      const freshSelection =
        next?.[identifiant] !== null
        && next?.[identifiant] !== undefined
        && next?.[identifiant] !== previous[identifiant];

      if (libelleChanged && !freshSelection) {
        dependants.forEach((acteKey) => {
          referentiel[acteKey] = null;
        });
      }
    },
  );

  return referentiel;
};

// Sens inverse : réinjecte les informations du référentiel dans l'état du
// formulaire. Indispensable, car `AdresseForm` fait un `setAdresse(clone(props))`
// à chaque changement de ses props (remplacement complet, pas une fusion) :
// sans ces clés, les identifiants captés à la sélection d'une voie seraient
// effacés au render suivant côté assistant, et remontés en `undefined` côté
// fiche dès qu'un autre champ est modifié après réouverture de la modale.
const toReferentielFormValue = (adresse: any) => ({
  identifiantVoie: adresse?.identifiantVoie ?? null,
  identifiantTypeVoie: adresse?.identifiantTypeVoie ?? null,
  // Attention : `typeVoie` non vide arme l'effet de montage d'AdresseFormVoie
  // (préfixage de la voie par son type) quand le champ Voie est en saisie
  // libre, c.-à-d. hors type « Commune » : il émettrait un `onChange` à chaque
  // ouverture. Les appelants le vident donc dans ces types.
  typeVoie: adresse?.typeVoie ?? '',
  lieuDit: adresse?.lieuDit ?? '',
  identifiantCommune: adresse?.identifiantCommune ?? null,
  identifiantCodePostal: adresse?.identifiantCodePostal ?? null,
  identifiantDepartement: adresse?.identifiantDepartement ?? null,
  departement: adresse?.libelleDepartement ?? '',
});

// Voie côté AdresseForm : depuis les champs granulaires de l'API dès que l'un
// d'eux est renseigné (aucun découpage à faire, donc aucune perte), sinon en
// découpant la ligne `adresse` des conventions historiques.
const toVoieFormValue = (adresse: any) => {
  const numeroVoie = toNumeroVoie(adresse?.numeroVoie);
  const complementNumeroVoie = toText(adresse?.complementNumeroVoie);
  const libelleVoie = toText(adresse?.libelleVoie);

  if (!numeroVoie && !complementNumeroVoie && !libelleVoie) {
    // Seule la première ligne porte la voie (la seconde est « CP LOCALITÉ »).
    const [firstLine] = toText(adresse?.adresse).split(/\r?\n/);
    const parsed = parseAdresseLine(firstLine);
    // « 0 83190 Ollioules » (composée par l'API sans voie) : le code postal
    // n'est pas une voie.
    const voie = /^\d{5}\b/.test(parsed.voie) ? '' : parsed.voie;

    return {
      ...parsed,
      numeroVoirie: toNumeroVoie(parsed.numeroVoirie),
      voie: stripTypeVoie(voie, adresse?.typeVoie),
    };
  }

  return {
    numeroVoirie: numeroVoie,
    // Pas de champ dédié côté API : l'indice reste dans la ligne `adresse`.
    indiceRepetition: '',
    complementNumeroVoirie: complementNumeroVoie,
    voie: stripTypeVoie(libelleVoie, adresse?.typeVoie),
  };
};

// Le type d'adresse enregistré pilote AdresseForm (`handleTypes`) : en
// « Commune », Voie passe en recherche assistée avec le type de voie en
// étiquette ; dans les autres types, tout est en saisie libre et le type de voie
// reste dans le libellé (cf. estTypeAdresseCommune).
export const toAdresseFormValue = (
  adresse: any,
  options: AdresseFormValueOptions = {},
) => {
  const { articleField = 'lettre' } = options;
  const type = toTypeAdresseLabel(adresse?.typeAdresse);
  // Sans type de voie, ni découpage du libellé ni préfixage au montage du
  // champ Voie en saisie libre (cf. toReferentielFormValue).
  const adresseVoie = estTypeAdresseCommune(type)
    ? adresse
    : { ...adresse, typeVoie: '' };

  return {
    type,
    codePostal: adresse?.codePostal ?? '',
    commune: adresse?.localite ?? '',
    pays: adresse?.libellePays ?? '',
    // Lu sur le même champ que celui où `fromAdresseFormValue` réécrira : lire
    // `lettre` pour écrire ensuite dans `articleLocalite` laisserait `lettre`
    // périmé avec une autre valeur.
    article: adresse?.[articleField] ?? '',
    // Recalculé par AdresseForm dès qu'une commune/CP/voie est sélectionnée
    // (cf. fromAdresseFormValue) : seedé ici pour ne pas perdre la valeur déjà
    // enregistrée tant que l'adresse n'a pas été retouchée.
    codeDepartement: adresse?.codeDepartement ?? '',
    ...toVoieFormValue(adresseVoie),
    ...toReferentielFormValue(adresseVoie),
  };
};

/**
 * Équivalent de `toAdresseFormValue` pour l'assistant de création, dont
 * `AdresseForm` est piloté en type « Commune » et avec les champs de
 * complément. Factorisé ici parce que ce littéral était recopié à l'identique
 * dans trois JSX, où aucun test ne peut l'atteindre (le mock d'`AdresseForm`
 * ignore la prop `adresse`).
 */
export const toAssistantAdresseFormValue = (
  adresse: any,
  typesVoies: string[] = [],
) => {
  // Type choisi par l'utilisateur : le fixer à « Commune » le réimposait à
  // chaque rendu (AdresseForm recopie ses props dans son état).
  const type = adresse?.type || TYPE_ADRESSE_COMMUNE;
  // Voie hors référentiel : l'API ne renvoie pas de type, il est déduit du
  // libellé. Seulement en « Commune » (cf. estTypeAdresseCommune).
  const typeVoie = estTypeAdresseCommune(type)
    ? adresse?.typeVoie || findTypeVoie(adresse?.libelleVoie, typesVoies)
    : null;

  return {
    type,
    codePostal: adresse?.codePostal,
    commune: adresse?.localite,
    voie: stripTypeVoie(adresse?.libelleVoie, typeVoie),
    pays: adresse?.libellePays,
    article: adresse?.lettre,
    numeroVoirie: toNumeroVoie(adresse?.numeroVoie),
    complementNumeroVoirie: adresse?.complementNumeroVoie,
    complementGeographique: adresse?.complementGeographique,
    complementDistribution: adresse?.complementDistribution,
    complementIdentification: adresse?.complementIdentification,
    ...toReferentielFormValue(adresse),
    typeVoie: typeVoie ?? '',
  };
};

/**
 * Recompose la ligne de rue « 12 Bis B Rue de la Paix » à partir des champs
 * séparés d'`AdresseForm`. Exporté pour l'adresse de résidence des partenaires,
 * dont le champ texte (`residenceAdresse`) n'est pas recalculé côté back et
 * doit donc être réécrit par le front à chaque enregistrement.
 */
export const buildAdresseLine = (adresseFormState: any): string =>
  [
    adresseFormState?.numeroVoirie,
    adresseFormState?.indiceRepetition,
    adresseFormState?.complementNumeroVoirie,
    adresseFormState?.voie,
  ]
    .map(toText)
    .filter(Boolean)
    .join(' ');

/**
 * Recompose l'adresse de convention à partir de l'état complet renvoyé par
 * `AdresseForm` (`onItemChange`, toujours l'état complet, jamais un diff).
 *
 * Écrit les champs granulaires attendus par l'API (`numeroVoie`,
 * `complementNumeroVoie`, `libelleVoie`) ; `adresse` est conservé pour les
 * conventions qui ne stockent que la ligne de rue.
 *
 * Le type de voie (« Rue »), renvoyé à part par `AdresseForm`, est réintégré
 * en tête du libellé (cf. `composeLibelleVoie`).
 */
const fromAdresseFormValue = (
  adresseFormState: any,
  options: FromReferentielOptions = {},
) => {
  const referentiel = fromAdresseFormReferentiel(adresseFormState, options);
  // Type invalidé (`null`) quand la voie a été retapée à la main : le texte
  // saisi fait alors foi tel quel.
  const typeVoie =
    'typeVoie' in referentiel ? referentiel.typeVoie : adresseFormState?.typeVoie;
  // Hors « Commune », la voie saisie contient déjà son type.
  const voie = estTypeAdresseCommune(adresseFormState?.type)
    ? composeLibelleVoie(typeVoie, adresseFormState?.voie)
    : toText(adresseFormState?.voie);

  return {
    // Absent (formulaire sans choix du type) : l'API garde celui enregistré,
    // ainsi que le pays.
    ...(toText(adresseFormState?.type) && {
      typeAdresse: toTypeAdresseCode(adresseFormState.type),
      libellePays:
        paysTypeAdresse(adresseFormState.type) ?? toText(adresseFormState.pays),
    }),
    adresse: buildAdresseLine({ ...adresseFormState, voie }),
    numeroVoie: toText(adresseFormState?.numeroVoirie),
    complementNumeroVoie: toText(adresseFormState?.complementNumeroVoirie),
    libelleVoie: voie,
    codePostal: adresseFormState?.codePostal ?? '',
    localite: adresseFormState?.commune ?? '',
    // AdresseForm recalcule codeDepartement dès que la commune, le code postal
    // ou la voie change (recherche adresse/commune) : sans ce champ, l'ancien
    // département restait envoyé après changement de commune.
    codeDepartement: adresseFormState?.codeDepartement ?? '',
    ...referentiel,
  };
};

export default fromAdresseFormValue;

const ADRESSES_CONVENTION_AVEC_VOIE = [
  'adresseConvention',
  'adresseDeclaration',
  'adresseCommune',
] as const;

const PARTENAIRES = ['partenaire1', 'partenaire2'] as const;

const withLibelleVoieCompose = (adresse: any) =>
  _.isNil(adresse)
    ? adresse
    : {
        ...adresse,
        libelleVoie:
          (estTypeAdresseCommune(adresse.type)
            ? composeLibelleVoie(adresse.typeVoie, adresse.libelleVoie)
            : toText(adresse.libelleVoie)) || null,
        typeAdresse: toTypeAdresseCode(adresse.type),
      };

/**
 * Prépare la convention de l'assistant pour l'API : l'assistant garde le type
 * de voie à part du libellé (affiché en étiquette par `AdresseForm`), l'API
 * n'enregistre que le libellé (cf. `composeLibelleVoie`). Le type d'adresse
 * (libellé `AdresseForm`) est envoyé en code (cf. `toTypeAdresseCode`).
 */
export const composeLibellesVoieConvention = (conventionDeclaration: any) => {
  if (_.isNil(conventionDeclaration)) return conventionDeclaration;

  const result = { ...conventionDeclaration };

  ADRESSES_CONVENTION_AVEC_VOIE.forEach((key) => {
    result[key] = withLibelleVoieCompose(result[key]);
  });

  PARTENAIRES.forEach((key) => {
    if (_.isNil(result[key])) return;

    result[key] = {
      ...result[key],
      adresseResidence: withLibelleVoieCompose(result[key].adresseResidence),
    };
  });

  return result;
};
